"use client";

import { createContext, useContext, useState, useRef, useCallback } from "react";
import type { NavItemConfig } from "@/lib/navigation-utils";

interface MegaMenuContextType {
  activeDropdown: string | null;
  activeDropdownData: NavItemConfig | null;
  setActiveDropdown: (id: string | null, data?: NavItemConfig) => void;
  cancelClose: () => void;
}

const MegaMenuContext = createContext<MegaMenuContextType | null>(null);

interface MegaMenuProviderProps {
  children: React.ReactNode;
  items: NavItemConfig[];
}

export function MegaMenuProvider({ children, items }: MegaMenuProviderProps) {
  const [activeDropdown, setActiveDropdownState] = useState<string | null>(null);
  const [activeDropdownData, setActiveDropdownData] = useState<NavItemConfig | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const setActiveDropdown = useCallback((id: string | null, data?: NavItemConfig) => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    if (id === null) {
      // Delay closing to allow cursor to move to mega menu
      closeTimeoutRef.current = setTimeout(() => {
        setActiveDropdownState(null);
        setActiveDropdownData(null);
      }, 150);
    } else {
      // Open immediately
      setActiveDropdownState(id);
      setActiveDropdownData(data || items.find(item => item.id === id) || null);
    }
  }, [items]);

  const cancelClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  return (
    <MegaMenuContext.Provider value={{ activeDropdown, activeDropdownData, setActiveDropdown, cancelClose }}>
      {children}
    </MegaMenuContext.Provider>
  );
}

export function useMegaMenu() {
  const context = useContext(MegaMenuContext);
  if (!context) {
    throw new Error("useMegaMenu must be used within MegaMenuProvider");
  }
  return context;
}
